<template>
    <component
        :is="rootTag"
        ref="thSelect"
        v-qa.is.native="isNativeInternal"
        v-qa.model="JSON.stringify(modelValue)"
        :class="classThSelect"
        class="th-select"
        tabindex="0"
        @focus="open"
    >
        <component
            :is="legendTag"
            v-if="legend"
            class="legend"
        >
            {{ legend }}

            <slot name="tooltip" />
        </component>

        <div
            v-if="isNativeInternal"
            class="select-wrapper"
        >
            <select
                v-model="modelValue"
                :disabled
                class="select native"
            >
                <option
                    v-if="placeholder"
                    v-qa="'placeholder'"
                    :disabled="!clearable"
                    :value="undefined !== defaultValue ? defaultValue : ''"
                >
                    {{ placeholder }}
                </option>

                <option
                    v-for="(option, index) in filteredOptions"
                    :key="option.label.concat(String(index))"
                    :value="option.value"
                    class="option"
                >
                    {{ option.label }}
                </option>
            </select>
        </div>

        <div
            v-else
            ref="select"
            :class="classSelect"
            :disabled
            class="select"
            tabindex="0"
            @focus.prevent.stop="''"
            @click.prevent.stop="toggle"
            @keyup.space.prevent.stop="open"
            @keyup.escape.prevent.stop="close"
            @keydown.up.prevent.stop="pickPrevious"
            @keydown.down.prevent.stop="pickNext"
        >
            <div
                v-if="valLabel"
                class="picked"
            >
                {{ valLabel }}
            </div>

            <div
                v-else
                class="placeholder"
            >
                {{ placeholder }}
            </div>

            <transition name="fade">
                <div
                    v-if="isOpen"
                    class="datalist"
                >
                    <div
                        v-for="(option, index) in filteredOptions"
                        :key="option.label.concat(String(index))"
                        ref="optionElements"
                        :class="getClassOption(index)"
                        :data-qa-option="option.label"
                        :value="option.value"
                        class="option"
                        @click.prevent.stop="pick(option.value)"
                    >
                        {{ option.label }}
                    </div>

                    <div
                        v-if="!filteredOptions.length"
                        class="option empty"
                    >
                        Nothing here
                    </div>
                </div>
            </transition>

            <input
                v-if="isOpen"
                ref="filterInput"
                v-model="filter"
                class="filter"
                placeholder="Type to filter"
                type="text"
                @keydown.down.prevent.stop="highlightNextOption"
                @keydown.up.prevent.stop="highlightPreviousOption"
                @keydown.enter.prevent.stop="
                    pick(filteredOptions[highlightedOptionIndex]?.value)
                "
            />
            <!--                @focusout="closeWithoutFocus"-->
        </div>

        <FieldError
            v-if="error"
            :error="error"
        />
    </component>
</template>

<script lang="ts" setup generic="T">
import { computed, nextTick, type Ref, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'

import FieldError from '@/components/ui/inputs/ThFieldError.vue'
import type { SelectOption } from '@/components/ui/inputs/th-select.ts'
import { useIsMobileAgent } from '@/plugins/is-mobile-agent.ts'

defineSlots<{
    'tooltip': () => any
}>()

const {
    options,
    isNative,
    placeholder = undefined,
    legend = undefined,
    error = undefined,
    disabled,
    clearable,
    defaultValue = undefined,
    searcher = undefined,
    rootTag = 'fieldset',
    serifFont,
} = defineProps<{
    options: SelectOption<T>[] | Readonly<SelectOption<T>[]>
    placeholder?: string
    legend?: string
    error?: string
    disabled?: boolean
    clearable?: boolean
    defaultValue?: T | null
    searcher?: (value: T, term: string) => boolean
    // read it in the news!
    serifFont?: boolean
    isNative?: boolean
    rootTag?: 'fieldset' | 'div'
}>()

const modelValue = defineModel<T | null>('modelValue', { default: null })

const legendTag = computed<'div' | 'legend'>(() =>
    rootTag === 'div' ? 'div' : 'legend'
)

const valLabel = computed((): string | undefined => {
    const value = modelValue.value

    if (null === value) {
        return undefined
    }

    return options.find((option) => {
        if (typeof value === 'object') {
            return JSON.stringify(option.value) === JSON.stringify(value)
        }

        return option.value === value
    })?.label
})

const isMobileAgent = useIsMobileAgent()
const isNativeInternal = computed(() => {
    if (undefined !== isNative) {
        return isNative
    }

    return isMobileAgent
})

const isOpen = ref(false)
const filterInput = ref<HTMLInputElement | null>(null)

const open = () => {
    if (disabled) {
        return
    }

    isOpen.value = true

    nextTick(() => {
        filterInput.value?.focus()

        scrollOptionIntoView(highlightedOptionIndex.value)
    })
}

const select = ref<HTMLElement | null>(null)

const close = () => {
    isOpen.value = false

    if (select.value) {
        select.value.focus()
    }
}

const closeWithoutFocus = () => {
    isOpen.value = false
}

const toggle = () => (isOpen.value ? close() : open())

const thSelect: Ref<HTMLElement | null> = ref(null)
onClickOutside(thSelect, closeWithoutFocus)

const classSelect = computed(() => ({
    open: isOpen.value,
    disabled: disabled,
}))

const pick = (optionValue: T | null | undefined) => {
    if (disabled) {
        return
    }

    modelValue.value = undefined === optionValue ? null : optionValue

    highlightedOptionIndex.value = filteredOptions.value.findIndex(
        (option) => option.value === optionValue
    )

    filter.value = ''

    close()
}

const filter = ref('')
const filteredOptions = computed((): SelectOption<T | null>[] => {
    let o: SelectOption<T>[] = [...options]

    if (filter.value) {
        o = o.filter((option) => {
            if (
                option.label.toLowerCase().includes(filter.value.toLowerCase())
            ) {
                return true
            }

            if (typeof option.value === 'string') {
                return option.value
                    .toLowerCase()
                    .includes(filter.value.toLowerCase())
            }

            if (typeof option.value === 'number') {
                return option.value.toString().includes(filter.value)
            }

            if (searcher) {
                return searcher(option.value, filter.value)
            }

            return true
        })
    }

    if (
        !isNativeInternal.value &&
        placeholder &&
        clearable &&
        undefined !== defaultValue
    ) {
        return [
            {
                label: placeholder,
                value: defaultValue,
            },
            ...o,
        ]
    }

    return o
})

const highlightedOptionIndex = ref(
    filteredOptions.value.findIndex(
        (option) => option.value === modelValue.value
    )
)

const optionElements = ref<HTMLElement[]>([])

const scrollOptionIntoView = (optionIndex: number) => {
    const optionElement = optionElements.value[optionIndex]

    if (!optionElement) {
        return
    }

    optionElement.scrollIntoView({
        block: 'nearest',
    })
}

const highlightNextOption = () => {
    if (highlightedOptionIndex.value < filteredOptions.value.length - 1) {
        highlightedOptionIndex.value += 1
    } else {
        highlightedOptionIndex.value = 0
    }

    scrollOptionIntoView(highlightedOptionIndex.value)
}

const highlightPreviousOption = () => {
    if (highlightedOptionIndex.value > 0) {
        highlightedOptionIndex.value -= 1
    } else {
        highlightedOptionIndex.value = filteredOptions.value.length - 1
    }

    scrollOptionIntoView(highlightedOptionIndex.value)
}

const pickNext = () => {
    if (highlightedOptionIndex.value < filteredOptions.value.length - 1) {
        pick(filteredOptions.value[highlightedOptionIndex.value + 1]?.value)
    } else {
        pick(filteredOptions.value[0]?.value)
    }
}

const pickPrevious = () => {
    if (highlightedOptionIndex.value > 0) {
        pick(filteredOptions.value[highlightedOptionIndex.value - 1]?.value)
    } else {
        pick(filteredOptions.value[filteredOptions.value.length - 1]?.value)
    }
}

const getClassOption = (index: number) => {
    return {
        'highlighted': index === highlightedOptionIndex.value,
    }
}

const classThSelect = computed(() => ({
    'error': error,
    'serif-font': serifFont,
}))
</script>

<style lang="scss" scoped>
.th-select {
    width: 100%;
    font-family: $effra-font-stack;
    font-weight: 400;
    letter-spacing: 0.05em;

    &.serif-font {
        font-family: $sectra-font-stack;
        letter-spacing: unset;
    }

    .legend {
        font-family: $effra-font-stack;
        font-weight: 400;
        display: flex;
        font-size: 0.75rem;
        text-transform: uppercase;
        color: $slate;
        margin-bottom: 5px;
        line-height: 20px;
        align-items: center;

        :deep(.tooltip-trigger) {
            &::after {
                margin-left: 5px;
                font-size: 1rem;
                height: 16px;
                width: 16px;
                line-height: 1em;
            }
        }
    }

    .filter {
        position: relative;
        border-radius: 2px;
        width: 100%;
        height: 100%;
        padding-bottom: 2px;
    }

    input[type='text'] {
        background-color: $white;
    }

    .select-wrapper {
        position: relative;

        .native {
            position: relative;
            z-index: 2;
            background: transparent;
        }

        &::after {
            display: block;
            font-family: $icon-font;
            content: $icon-angle-down;
            font-size: 0.675rem;
            font-weight: 700;
            position: absolute;
            right: 0;
            top: 0;
            width: 50px;
            height: 50px;
            line-height: 50px;
            z-index: 1;
            text-align: center;
        }
    }

    .select {
        position: relative;
        display: block;
        width: 100%;
        height: 50px;
        padding: 15px 50px 15px 10px;
        border: 1px solid rgba($slate, 0.5);
        border-radius: 2px;
        font-size: 1rem;
        line-height: 1.25rem;
        cursor: pointer;

        &::after {
            display: block;
            font-family: $icon-font;
            content: $icon-angle-down;
            font-size: 0.675rem;
            font-weight: 700;
            position: absolute;
            right: 0;
            top: 0;
            width: 50px;
            height: 50px;
            line-height: 50px;
            text-align: center;
        }

        datalist {
            appearance: none;
            display: none;
        }

        .picked,
        .placeholder {
            padding: 15px 40px 15px 10px;
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
        }

        .placeholder {
            color: $grey-400;
        }

        &.open {
            border-bottom: 0;
            border-radius: 2px 2px 0 0;
            background-color: $white;
            z-index: 1000;
        }

        &:focus {
            border-color: $slate;
        }

        &.disabled {
            border-color: $grey-300;
            color: $grey-400;
        }

        &.native {
            padding-top: 14px;
            white-space: nowrap;
        }

        .datalist {
            position: absolute;
            right: -1px;
            left: -1px;
            top: 49px;
            background-color: $white;
            border: 1px solid rgba($slate, 0.5);
            border-top: none;
            border-radius: 0 0 2px 2px;
            max-height: 330px;
            min-width: 300px;
            overflow: auto;

            .option {
                min-height: 50px;
                width: inherit;
                border-top: 1px solid $grey-200;
                background-color: $white;
                display: flex;
                align-items: center;
                font-style: normal;
                font-weight: 400;
                font-size: 0.875rem;
                line-height: 20px;
                cursor: pointer;
                padding: 15px 10px;

                &.highlighted {
                    background-color: $blue;
                    color: $white;
                }

                &:not(.highlighted) {
                    @include hover() {
                        background-color: $grey-200;
                    }
                }

                &.empty {
                    color: $grey-400;
                    cursor: not-allowed;
                }
            }
        }
    }

    &.error {
        .select {
            border-color: rgba($red, 0.5);
        }

        .datalist {
            border-color: rgba($red, 0.5);
        }
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
